// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import * as ss from 'styled-system';
import CloseIcon from 'close.svg';
import Box from 'components/Box';
import Spinner from 'components/Spinner';
import Icon from 'components/Icon';
import { ChevronDownIcon, ChevronUpIcon } from 'components/ChevronIcon';
import { Link as RouterLink } from 'components/RouterLink';
import TrackedLink from 'components/TrackedLink';
import Tooltip, { DefaultButton as TooltipButton } from 'components/Tooltip';
import TabContext from 'containers/TabContext';
import {
  colors,
  fontWeights,
  fonts,
  bodyMicroText,
  transition,
  mediaRetina
} from '../theme';

const size = props => {
  switch (props.size) {
    case 'small':
      return {
        fontSize: props.fontSize || '14px',
        padding: !props.p && '0.4em 1.2em'
      };
    case 'large':
      return {
        fontSize: props.fontSize || '18px',
        padding: !props.p && '12px 24px'
      };
    default:
      return {
        fontSize: props.fontSize || '16px',
        padding: !props.p && '8px 16px'
      };
  }
};

const StyledButton = styled(
  ({
    as,
    component,
    type = 'button',
    isTabbing,
    m,
    mt,
    mr,
    mb,
    ml,
    mx,
    my,
    p,
    pt,
    pr,
    pb,
    pl,
    px,
    py,
    display,
    alignItems,
    justifyContent,
    width,
    height,
    minWidth,
    minHeight,
    color,
    bg,
    fontSize,
    fontWeight,
    borderRadius,
    border,
    ...props
  }: {
    as?: string,
    component?: React.AbstractComponent<any>,
    type?: string,
    isTabbing?: boolean
  }) =>
    as ? (
      React.createElement(as, props)
    ) : component ? (
      React.createElement(component, props)
    ) : (
      <button {...props} type={type} />
    )
)`
  padding: 0;
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  &[disabled] {
    cursor: auto;
  }
  ${props =>
    !props.isTabbing &&
    `
    outline: 0;
  `}
  ${ss.size} ${ss.display} ${ss.space} ${ss.width} ${ss.height} ${
  ss.minWidth
} ${ss.minHeight} ${ss.fontSize} ${ss.fontWeight} ${ss.borderRadius} ${
  ss.color
} ${ss.border}
`;

const Button = (props: any) => (
  <TabContext.Consumer>
    {isTabbing => <StyledButton {...props} isTabbing={isTabbing} />}
  </TabContext.Consumer>
);

export default Button;

export const LinkButton = styled(({ danger, grey, ...props }) => (
  <Button {...props} />
))`
  font-family: ${fonts.sans};
  font-weight: ${fontWeights.semiBold};
  font-size: 18px;
  color: ${props =>
    props.danger
      ? colors.scarlet
      : props.grey
      ? colors.battleshipGrey
      : colors.waterBlue};
  transition: all ${transition};
  &:hover,
  &:focus {
    color: ${props => (props.danger ? colors.brick : colors.niceBlue)};
  }
  &:hover {
    text-decoration: underline;
  }
`;

export const buttonStyles = css`
  -webkit-font-smoothing: antialiased;
  display: inline-block;
  vertical-align: middle;
  text-align: center;
  text-decoration: none;
  font-family: inherit;
  font-weight: ${fontWeights.semiBold};
  letter-spacing: 0.5px;
  line-height: 1.3;
  cursor: pointer;
  border-radius: ${props =>
    !props.square ? '9999px' : props.size === 'small' ? '4px' : '7px'};
  transition: all ${transition};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  ${size}
  ${ss.borderRadius}
  ${ss.boxShadow}
`;

export const ButtonBase = styled(props => {
  const {
    size: sizeProp,
    boxShadow,
    borderRadius,
    reverse,
    blue,
    green,
    square,
    light,
    ...buttonProps
  } = props;
  return <Button {...buttonProps} />;
}).attrs(props => ({
  type: props.type || 'button'
}))`
  ${buttonStyles}
`;

export const PrimaryButton = styled(ButtonBase)`
  transition: all ${transition};
  text-decoration: none;
  ${props => `
    color: ${props.color || (props.reverse ? colors.waterBlue : colors.white)};
    background: ${
      props.reverse
        ? colors.white
        : props.green
        ? colors.turtleGreen
        : colors.waterBlue
    };
  `}
  &:hover {
    background-color: ${props =>
      props.green ? colors.waterBlue : colors.turtleGreen};
    color: ${colors.white};
    text-decoration: none;
  }
  &:disabled {
    &:hover {
      background-color: ${colors.waterBlue};
    }
  }
`;
/* eslint-disable react/no-multi-comp */
export const PrimaryButtonLink = React.forwardRef<any, PrimaryButton>(
  (props, ref) => (
    <PrimaryButton component="a" type={undefined} ref={ref} {...props} />
  )
);
export const TrackedPrimaryButtonLink = (props: Object) => (
  <PrimaryButton component={TrackedLink} type={undefined} {...props} />
);
export const PrimaryButtonRouterLink = React.forwardRef<any, PrimaryButton>(
  (props, ref) => (
    <PrimaryButton
      component={RouterLink}
      type={undefined}
      ref={ref}
      {...props}
    />
  )
);

export const PrimaryButtonDanger = styled(ButtonBase)`
  color: ${colors.white};
  background-color: ${colors.scarlet};
  &:hover,
  &:focus {
    background-color: ${colors.brick};
  }
`;

export const SecondaryButton = styled(ButtonBase)`
  ${({ blue, reverse, light }) => {
    const buttonTheme = {
      textColor: reverse
        ? 'white'
        : blue
        ? colors.waterBlue
        : colors.battleshipGrey,
      borderColor: reverse
        ? 'white'
        : blue
        ? colors.waterBlue
        : light
        ? colors.aluminum
        : colors.battleshipGrey,
      hoverTextColor: reverse ? colors.waterBlue : colors.turtleGreen,
      hoverBorderColor: reverse ? 'white' : colors.turtleGreen,
      hoverBgColor: reverse ? 'white' : 'transparent'
    };
    return `
      color: ${buttonTheme.textColor};
      box-shadow: inset 0 0 0 1px ${buttonTheme.borderColor};
      &:not([disabled]):hover {
        color: ${buttonTheme.hoverTextColor};
        background-color: ${buttonTheme.hoverBgColor};
        box-shadow: inset 0 0 0 1px ${buttonTheme.hoverBorderColor};
        text-decoration: none;
        &:disabled {
          &:hover {
            background-color: transparent;
            box-shadow: inset 0 0 0 1px ${buttonTheme.borderColor};
          }
        }
      }
      @media ${mediaRetina} {
        box-shadow: inset 0 0 0 1.5px ${buttonTheme.borderColor};
        &:not([disabled]):hover {
          box-shadow: inset 0 0 0 1.5px ${buttonTheme.hoverBorderColor};
        }
      }
    `;
  }}
`;

export const SecondaryButtonLink = React.forwardRef<any, SecondaryButton>(
  (props, ref) => (
    <SecondaryButton component="a" type={undefined} ref={ref} {...props} />
  )
);

export const TrackedSecondaryButtonLink = (props: Object) => (
  <SecondaryButton component={TrackedLink} type={undefined} {...props} />
);

export const SecondaryButtonRouterLink = React.forwardRef<any, SecondaryButton>(
  (props, ref) => (
    <SecondaryButton
      component={RouterLink}
      type={undefined}
      ref={ref}
      {...props}
    />
  )
);

export const SecondaryButtonDanger = styled(ButtonBase)`
  color: ${colors.scarlet};
  background-color: ${colors.white};
  box-shadow: inset 0 0 0 1px ${colors.scarlet};
  @media ${mediaRetina} {
    box-shadow: inset 0 0 0 1.5px ${colors.scarlet};
  }
`;

export const SecondaryButtonDangerRouterLink = React.forwardRef<
  any,
  SecondaryButtonDanger
>((props, ref) => (
  <SecondaryButtonDanger
    component={RouterLink}
    type={undefined}
    ref={ref}
    {...props}
  />
));

export const SecondaryButtonDangerLink = React.forwardRef<
  any,
  SecondaryButtonDanger
>((props, ref) => (
  <SecondaryButtonDanger component="a" type={undefined} ref={ref} {...props} />
));
/* eslint-enable */

export const InactiveButton = styled(props => (
  <ButtonBase component={Box} {...props} />
))`
  box-shadow: inset 0 0 0 1px ${colors.silver};
  color: ${colors.battleshipGrey};
  @media ${mediaRetina} {
    box-shadow: inset 0 0 0 1.5px ${colors.silver};
    cursor: auto;
  }
`;

export const BorderlessButton = styled(ButtonBase)`
  color: ${colors.battleshipGrey};
  background-color: transparent;
  border-radius: 0;
`;

export const CircleButton = styled(ButtonBase)`
  border-radius: ${props => props.size};
  border: 1px solid ${props => props.color};
  color: ${props => props.color};
  width: ${props => props.size};
  height: ${props => props.size};
  box-shadow: none;
  background: none;
  border-color: ${props => props.borderColor};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

const StyledCloseButton = styled(ButtonBase)`
  padding: 8px 16px;
  color: ${colors.battleshipGrey};
  &:hover {
    color: ${colors.waterBlue};
    &:disabled {
      &:hover {
        color: ${colors.battleshipGrey};
      }
    }
  }
  background-color: transparent;
  border-radius: 0;
`;

export const CloseButton = ({
  width = '13',
  height = '12',
  ...props
}: {
  width?: string,
  height?: string
}) => (
  <StyledCloseButton p={0} {...props}>
    <CloseIcon width={width} height={height} />
  </StyledCloseButton>
);

export const ClearButton = styled(CloseButton)`
  display: inline-block;
  position: absolute;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  right: 17px;
  padding-left: 3px;
  padding-right: 3px;
`;

export const TextButton = styled(Button)`
  padding: 0;
  background: none;
  border: 0;
  appearance: none;
  font-size: 14px;
  font-family: ${fonts.sans};
  letter-spacing: 0.44px;
  font-weight: ${fontWeights.semiBold};
  ${props => `
    color: ${props.reverse ? 'white' : colors.waterBlue};
  `}
  &[disabled] {
    color: ${colors.battleshipGrey};
    opacity: 0.5;
  }
  &:not([disabled]) {
    cursor: pointer;
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`;

export const CallToActionButton = ({
  loggedIn,
  loggedInText = 'Apply',
  loggedOutText = 'Sign Up',
  hasGradProgram,
  hasGradProgramText = 'Edit Grad Program',
  ...props
}: {
  loggedIn: boolean,
  loggedInText?: string,
  loggedOutText?: string,
  hasGradProgram: boolean,
  hasGradProgramText?: string
}) => {
  const ctaText = hasGradProgram
    ? hasGradProgramText
    : loggedIn
    ? loggedInText
    : loggedOutText;
  return (
    <PrimaryButtonRouterLink to="/manage-grad-program" {...props}>
      {ctaText}
    </PrimaryButtonRouterLink>
  );
};

export const ChevronDownButton = (props: Object) => (
  <BorderlessButton {...props}>
    <ChevronDownIcon />
  </BorderlessButton>
);

export const ChevronUpButton = (props: Object) => (
  <BorderlessButton {...props}>
    <ChevronUpIcon />
  </BorderlessButton>
);

export const DotNavigationButton = styled(({ goToPage, index, className }) => {
  const clickHandler = () => {
    goToPage(index);
  };
  const title = `${getText('Go to page')} ${index + 1}`;
  return (
    <Button
      onClick={clickHandler}
      className={className}
      aria-label={title}
      title={title}
    >
      <Box pb="100%" />
    </Button>
  );
})`
  width: 12px;
  padding: 0;
  margin: 0 4px;
  background: white;
  border: 0;
  appearance: none;
  border-radius: 50%;
  box-shadow: 0 0 0 1px ${colors.waterBlue} inset;
  transition: all ${transition};
  ${({ isActive }) =>
    isActive &&
    `
    background: ${colors.waterBlue};
  `}
`;

export const SecondaryToolbarButton = styled(SecondaryButtonLink)`
  background: transparent;
  color: white;
  box-shadow: inset 0 0 0 1px white;
  &:hover,
  &:focus {
    background: white;
    box-shadow: inset 0 0 0 1px white;
    color: ${colors.waterBlue};
  }
  @media ${mediaRetina} {
    box-shadow: inset 0 0 0 1.5px white;
    &:hover,
    &:focus {
      box-shadow: inset 0 0 0 1.5px white;
    }
  }
`;

export const ExpandButton = styled(
  ({ isActive, blue, grey, small, ...props }) => <Button {...props} />
)`
  position: relative;
  background: ${props =>
    props.blue ? colors.waterBlue : props.grey ? colors.platinum : 'white'};
  width: ${props => (props.small ? 24 : 40)}px;
  height: ${props => (props.small ? 24 : 40)}px;
  border-radius: 50%;
  transition: all ${transition};
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: ${props => (props.small ? 8 : 14)}px;
    height: 1px;
    border-radius: 1px;
    background: ${props =>
      props.blue
        ? 'white'
        : props.grey
        ? colors.waterBlue
        : colors.greyishBrown};
    transition: all ${transition};
  }
  &:hover, &:focus {${props =>
    props.blue
      ? `background: ${colors.turtleGreen}`
      : props.grey
      ? `
    background: ${colors.waterBlue};
    &::before, &::after {
      background: white;
    }
      `
      : `
    &::before, &::after {
      background: ${colors.turtleGreen};
    }`}
  }
  ${props =>
    props.isActive
      ? `
    &::before {
      transform: translate(-50%, -50%) translateZ(0);
    }
    &::after {
      transform: translate(-50%, -50%) rotate(180deg) translateZ(0);
    }
  `
      : `
    &::before {
      transform: translate(-50%, -50%) rotate(-90deg) translateZ(0);
    }
    &::after {
      transform: translate(-50%, -50%) translateZ(0);
    }
  `}
  @media ${mediaRetina} {
    &::before, &::after {
      height: 1.5px;
      border-radius: 2px;
    }
  }
`;

export const DeleteButton = styled(({ isActive, ...props }) => (
  <Button {...props}>
    <Box
      width="40px"
      height="40px"
      classes={['flex', 'align-center', 'justify-center']}
    >
      <Icon name="trashcan" width={14} />
    </Box>
  </Button>
))`
  border-radius: 50%;
  color: ${colors.greyishBrown};
  transition: all ${transition};
  svg {
    * {
      fill: currentColor;
    }
  }
  &:hover,
  &:focus {
    color: ${colors.scarlet};
  }
  ${props =>
    props.isActive &&
    `
    color: white;
    &:hover, &:focus {
      background: ${colors.scarlet};
      color: white;
    }
  `}
`;

const authButtonStyles = css`
  ${props =>
    props.size !== 'large'
      ? `
    ${
      props.$type === 'google'
        ? `padding-left: 12px;`
        : `
      padding: 7px 16px 7px 9px;`
    };
    ${bodyMicroText}
    font-weight: ${fontWeights.semiBold};
  `
      : `
    height: 47px;
    `};
  background: white;
  color: ${props => (props.$danger ? colors.scarlet : colors.battleshipGrey)};
  box-shadow: 0 0 0 1px
    ${props => (props.$danger ? colors.scarlet : colors.aluminum)} inset;
  svg {
    transition: fill ${transition};
  }
  &:hover,
  &:focus {
    color: ${props => (props.$danger ? 'white' : colors.battleshipGrey)};
    background: ${props => (props.$danger ? colors.scarlet : colors.platinum)};
    box-shadow: 0 0 0 1px
      ${props => (props.$danger ? colors.scarlet : colors.battleshipGrey)} inset;
    &[disabled] {
      background: white;
      color: ${props =>
        props.$danger ? colors.scarlet : colors.battleshipGrey};
      box-shadow: 0 0 0 1px
        ${props => (props.$danger ? colors.scarlet : colors.aluminum)} inset;
    }
    ${props =>
      props.$danger
        ? `
    &:not([disabled]) {
      svg {
        * {
          fill: white;
        }
      }
    }
    `
        : ''}
  }
  @media ${mediaRetina} {
    box-shadow: 0 0 0 1.5px
      ${props => (props.$danger ? colors.scarlet : colors.aluminum)} inset;
    &:hover,
    &:focus {
      box-shadow: 0 0 0 1.5px
        ${props => (props.$danger ? colors.scarlet : colors.battleshipGrey)}
        inset;
      &[disabled] {
        box-shadow: 0 0 0 1.5px
          ${props => (props.$danger ? colors.scarlet : colors.aluminum)} inset;
      }
    }
  }
`;

export const FacebookButton = styled(({ children, ...props }) => (
  <PrimaryButtonLink {...props} pl="6px">
    <Box width={1} display="flex" alignItems="center" justifyContent="center">
      <Icon name="facebook-new" width={22} height={22} />
      <Box ml={12}>{children || getText('Continue with Facebook')}</Box>
    </Box>
  </PrimaryButtonLink>
))`
  ${authButtonStyles};
`;

export const GoogleButton = styled(({ children, ...props }) => (
  <PrimaryButtonLink {...props}>
    <Box width={1} display="flex" alignItems="center" justifyContent="center">
      <Icon name="google" width={20} height={20} />
      <Box ml={12}>{children || getText('Continue with Google')}</Box>
    </Box>
  </PrimaryButtonLink>
))`
  ${authButtonStyles};
`;

export const AddButton = styled(props => (
  <Button {...props}>
    <Icon
      name="plus"
      stroke="currentColor"
      strokeWidth="6px"
      display="block"
      width={12}
    />
  </Button>
))`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px ${colors.waterBlue} inset;
  color: ${colors.waterBlue};
  transition: all ${transition};
  @media ${mediaRetina} {
    box-shadow: 0 0 0 1.5px ${colors.waterBlue} inset;
  }
  &:hover,
  &:focus {
    background: ${colors.waterBlue};
    color: white;
  }
`;

export const AddButtonLink = (props: Object) => (
  <AddButton component={RouterLink} {...props} />
);

const StyledTooltipButton = styled(TooltipButton)`
  color: currentColor;
  &::after {
    color: ${colors.waterBlue} !important;
    transition: none;
  }
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
    background: white;
    box-shadow: 0 0 0 2px ${colors.waterBlue} inset;
  }
`;

export const FormButtonLink = styled(
  ({
    children,
    tooltip,
    loading,
    icon = 'chevron',
    ...props
  }: {
    children: React.Node,
    tooltip?: string,
    loading?: boolean,
    icon?: string
  }) => (
    <LinkButton {...props}>
      <Box display="flex" alignItems="center">
        {children}
        {tooltip && (
          <Box display="inline-block" ml="6px">
            <Tooltip component="div" button={<StyledTooltipButton />}>
              {tooltip}
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box flex="0 0 auto" ml="8px">
        {loading ? (
          <Box width={18}>
            <Spinner size="small" />
          </Box>
        ) : (
          icon &&
          (icon === 'arrow' ? (
            <Icon name="arrow" fill="currentColor" width={14} height={14} />
          ) : (
            <Icon name="chevron" direction="right" fill="currentColor" />
          ))
        )}
      </Box>
    </LinkButton>
  )
)`
  min-height: 60px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px;
  margin: 12px 0;
  border-radius: 4px;
  box-shadow: 0 0 0 1px
    ${props => (props.$grey ? colors.silver : colors.waterBlue)} inset;
  color: ${props => (props.$grey ? colors.battleshipGrey : colors.waterBlue)};
  font-size: 18px;
  text-align: left;
  transition: all ${transition};
  &:hover,
  &:focus {
    text-decoration: none;
    box-shadow: 0 0 0 1px
      ${props => (props.$grey ? colors.battleshipGrey : colors.waterBlue)} inset;
    background: ${props =>
      props.$grey ? colors.battleshipGrey : colors.waterBlue};
    color: white;
    ${StyledTooltipButton} {
      color: ${props =>
        props.$grey ? colors.battleshipGrey : colors.waterBlue};
      background: white;
      box-shadow: 0 0 0 2px
        ${props => (props.$grey ? colors.battleshipGrey : colors.waterBlue)}
        inset;
      &::after {
        color: ${props =>
          props.$grey ? colors.battleshipGrey : colors.waterBlue};
      }
    }
  }
  @media ${mediaRetina} {
    box-shadow: inset 0 0 0 1.5px
      ${props => (props.$grey ? colors.silver : colors.waterBlue)};
    &:hover,
    &:focus {
      box-shadow: 0 0 0 1.5px
        ${props => (props.$grey ? colors.battleshipGrey : colors.waterBlue)}
        inset;
    }
  }
  &[disabled] {
    opacity: 0.7;
    pointer-events: none;
  }
  ${props =>
    props.loading &&
    `
    opacity: 0.7;
    pointer-events: none;
  `}
  * {
    transition: none;
  }
  ${StyledTooltipButton} {
    transition: all ${transition};
    transition-property: box-shadow, background;
  }
  em {
    font-weight: inherit;
  }
`;
